import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import GlobalDialog from '../../mui/Dialogue/GlobalDialog';
import { getProspectLeadAssessment } from '../../../redux/features/studentPortalSlice';

/**
 * "View Report" cell for the Student Portal Leads grid.
 *
 * Shows the full public parent-landing assessment for one lead. The grid can
 * only carry totals; the two things this exists to show do not fit in a column:
 *
 *   - LEVEL-WISE scores for the Class 1-4 foundation paper. 7/10 at Advanced and
 *     7/10 at Basic are very different children, and mark_scored collapses both
 *     to the same number.
 *   - QUESTION-WISE attended/correct for the LKG/UKG picture paper, INCLUDING
 *     the questions the child never attempted. For a four-year-old what they
 *     skipped is as informative as what they got wrong, so unattended rows are
 *     rendered rather than filtered out.
 */

const TRACK = {
  kg: { label: 'LKG / UKG picture paper', color: 'secondary' },
  foundation: { label: 'Foundation subject paper', color: 'primary' },
  senior: { label: 'Class 5+ — parent section only', color: 'default' },
};

const BAND_LABEL = { basic: 'Basic', standard: 'Standard', advanced: 'Advanced' };

const pct = (obtained, max) =>
  max > 0 ? Math.min(Math.round((Number(obtained) / Number(max)) * 100), 100) : 0;

const toneFor = (percent) => {
  if (percent >= 75) return '#2e7d32';
  if (percent >= 50) return '#ed6c02';
  return '#d32f2f';
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

/** Big score tile — one per section. */
const ScoreTile = ({ label, obtained, max, completed, at, emptyHint }) => {
  if (!completed) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderStyle: 'dashed' }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" color="text.disabled" sx={{ mt: 0.5 }}>
          Not attended
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {emptyHint}
        </Typography>
      </Paper>
    );
  }

  const percent = pct(obtained, max);
  const tone = toneFor(percent);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Chip size="small" label={`${percent}%`} sx={{ bgcolor: tone, color: '#fff', fontWeight: 700 }} />
      </Stack>
      <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: tone }}>
        {Number(obtained ?? 0)}
        <Typography component="span" variant="h6" color="text.secondary" fontWeight={600}>
          {' '}
          / {max ?? '—'}
        </Typography>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 8,
          borderRadius: 4,
          mt: 1.5,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: tone },
        }}
      />
      {formatDate(at) && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Completed {formatDate(at)}
        </Typography>
      )}
    </Paper>
  );
};

const SectionTitle = ({ children, caption }) => (
  <Box sx={{ mt: 4, mb: 1.5 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {children}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
    <Divider sx={{ mt: 1 }} />
  </Box>
);

/** One scored parent question: dimension, the answer given, and its 0-4 bar. */
const ScoredRow = ({ row }) => {
  const percent = pct(row.score, row.max_score);
  const tone = toneFor(percent);
  return (
    <Grid item xs={12} md={6}>
      <Paper variant="outlined" sx={{ p: 1.75 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
          <Typography variant="body2" fontWeight={700}>
            {row.question_group || row.question_text}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: tone }}>
            {row.score ?? 0}/{row.max_score}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {row.attended ? row.answer : 'Not answered'}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: tone },
          }}
        />
      </Paper>
    </Grid>
  );
};

const ReportBody = ({ lead, detail, loading }) => {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!detail) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Could not load the assessment for this lead.
      </Alert>
    );
  }

  const summary = detail.lead || {};
  const levels = detail.levels || [];
  const parentRows = detail.parentResponses || [];
  const studentRows = detail.studentResponses || [];

  // The five scored parent questions carry a max_score; the five profiling ones
  // do not. Splitting on that rather than on a hard-coded id list means adding a
  // question to either group needs no change here.
  const scored = parentRows.filter((r) => r.max_score !== null && r.max_score !== undefined);
  const profile = parentRows.filter((r) => r.max_score === null || r.max_score === undefined);

  const track = TRACK[summary.assessment_track];
  const initials = (lead?.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const statusIcon = (row) => {
    if (!row.attended) return <RemoveCircleOutlineIcon sx={{ color: 'grey.500', fontSize: 18 }} />;
    return row.is_correct ? (
      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
    ) : (
      <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
    );
  };

  return (
    <Box sx={{ pb: 1 }}>
      {/* ---- Identity ---- */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: 'primary.main', fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>
              {lead?.name || summary.name || 'Lead'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.class_name ? `Class ${summary.class_name}` : 'Class not given'}
              {summary.phone ? ` · ${summary.country_code || ''} ${summary.phone}` : ''}
              {summary.email ? ` · ${summary.email}` : ''}
            </Typography>
          </Box>
          {track && <Chip label={track.label} color={track.color} variant="outlined" />}
        </Stack>
      </Paper>

      {/* ---- Headline scores ---- */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ScoreTile
            label="Parent section"
            obtained={summary.parent_assessment_score}
            max={summary.parent_assessment_max ?? 20}
            completed={summary.parent_assessment_completed}
            at={summary.parent_assessment_at}
            emptyHint="This lead never started the assessment."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ScoreTile
            label="Student section"
            obtained={summary.mark_scored}
            max={summary.student_assessment_max}
            completed={summary.student_assessment_completed}
            at={summary.student_assessment_at}
            emptyHint={
              summary.parent_assessment_completed
                ? 'The parent answered their section but left before the question paper.'
                : 'Not reached.'
            }
          />
        </Grid>
      </Grid>

      {/* ---- Parent section ---- */}
      {parentRows.length > 0 && (
        <>
          <SectionTitle caption="How the parent rated their child. Higher is healthier.">
            Parent section
          </SectionTitle>
          <Grid container spacing={1.5}>
            {scored.map((row) => (
              <ScoredRow key={row.question_id} row={row} />
            ))}
          </Grid>

          {profile.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
                Profile
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These questions have no right answer and carry no score — they capture subject pain,
                learning style and intent for the sales conversation.
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {profile.map((row) => (
                  <Grid item xs={12} sm={6} md={4} key={row.question_id}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        {row.question_group || row.question_text}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
                        {row.attended ? row.answer : '—'}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {/* ---- Student section: foundation ---- */}
      {levels.length > 0 && (
        <>
          <SectionTitle caption="Each level the child attempted. Pass mark is 6 out of 10.">
            Level breakdown
          </SectionTitle>
          <Stack spacing={1.5}>
            {levels.map((lv) => {
              const percent = pct(lv.obtained, lv.total);
              const tone = lv.passed ? '#2e7d32' : '#ed6c02';
              return (
                <Paper variant="outlined" sx={{ p: 1.75 }} key={`${lv.subject}-${lv.level_key}`}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                        {lv.level_key === 'beginner' ? 'Basic' : lv.level_key}
                      </Typography>
                      {lv.subject && (
                        <Typography variant="caption" color="text.secondary">
                          {lv.subject}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700}>
                        {lv.obtained} / {lv.total}
                      </Typography>
                      <Chip
                        size="small"
                        label={lv.passed ? 'Passed' : 'Practice'}
                        color={lv.passed ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: tone },
                    }}
                  />
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      {/* ---- Student section: question by question (KG) ---- */}
      {studentRows.length > 0 && (
        <>
          <SectionTitle caption="Unanswered questions are shown too — what a young child skips matters as much as what they get wrong.">
            Question by question
          </SectionTitle>
          <Grid container spacing={1.5}>
            {studentRows.map((row) => (
              <Grid item xs={12} sm={6} key={row.question_id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    height: '100%',
                    borderColor: !row.attended
                      ? 'grey.300'
                      : row.is_correct
                      ? 'success.light'
                      : 'error.light',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ pt: 0.25 }}>{statusIcon(row)}</Box>
                    <Box flex={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="body2" fontWeight={700}>
                          {row.question_text}
                        </Typography>
                        {row.question_group && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={BAND_LABEL[row.question_group] || row.question_group}
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {row.attended ? `Answered: ${row.answer}` : 'Not attempted'}
                        {row.attended && row.is_correct === false && row.correct_answer
                          ? ` · Correct: ${row.correct_answer}`
                          : ''}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {parentRows.length === 0 && levels.length === 0 && studentRows.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          This lead was created but no assessment answers were recorded.
        </Alert>
      )}
    </Box>
  );
};

const ViewAssessmentReport = ({ params }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { leadAssessment, leadAssessmentLoading } = useSelector((state) => state.studentPortal);

  const lead = params?.row || {};
  const prospectLeadId = lead.prospect_lead_id;

  // GlobalDialog is keepMounted, so every row in the grid has one of these
  // mounted at once and they all read the SAME redux slice. Without this guard a
  // row would happily render whichever lead was fetched last. Only trust the
  // payload when it is the one this cell asked for.
  const isMine = Number(leadAssessment?.lead?.prospect_lead_id) === Number(prospectLeadId);
  const detail = isMine ? leadAssessment : null;

  const handleOpen = () => {
    if (!prospectLeadId) return;
    setOpen(true);
    dispatch(getProspectLeadAssessment(prospectLeadId));
  };

  return (
    <GlobalDialog
      title="Assessment Report"
      maxWidth="md"
      fullWidth
      closeButton
      open={open}
      setOpen={setOpen}
      popupButton={
        <Button
          size="small"
          variant="outlined"
          startIcon={<AssessmentIcon />}
          disabled={!prospectLeadId}
          onClick={handleOpen}
          sx={{ textTransform: 'none' }}
        >
          View report
        </Button>
      }
      content={
        // Only build the body for the dialog that is actually open. keepMounted
        // would otherwise render this for every row on every grid page.
        open ? (
          <ReportBody lead={lead} detail={detail} loading={leadAssessmentLoading || !detail} />
        ) : null
      }
      actionButton={
        <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      }
    />
  );
};

export default ViewAssessmentReport;
